import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, TextField, MenuItem, Button, Typography,
  Stack, Divider, Alert, CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useLocation } from 'react-router-dom';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import useCustomFields from '../../hooks/useCustomFields';
import ModuleCustomFields from '../dynamic/ModuleCustomFields';
import configService from '../../services/configService';

const CONTRACT_TYPES = [
  { value: 'indefinite',   label: 'Kontratë pa Afat (e Pacaktuar)' },
  { value: 'fixed_term',   label: 'Kontratë me Afat të Caktuar' },
  { value: 'probationary', label: 'Kontratë Provuese' },
  { value: 'part_time',    label: 'Kontratë me Kohë të Pjesshme' },
  { value: 'seasonal',     label: 'Kontratë Sezonale' },
  { value: 'internship',   label: 'Kontratë Praktike / Internship' },
];

const WORK_LOCATIONS = [
  { value: 'office',  label: 'Zyrë' },
  { value: 'remote',  label: 'Remote / Distancë' },
  { value: 'hybrid',  label: 'Hibride' },
  { value: 'field',   label: 'Terren' },
];

const MIN_SALARY = 264;

const today = () => new Date().toISOString().slice(0, 10);

export default function AddContract() {
  const history = useHistory();
  const location = useLocation();

  // Support pre-selecting a worker via ?worker=<id> URL param
  const preselectedWorkerId = new URLSearchParams(location.search).get('worker') || '';

  const [workers, setWorkers]       = useState([]);
  const [departments, setDepts]     = useState([]);
  const [templates, setTemplates]   = useState([]);
  const [saving, setSaving]         = useState(false);
  const [errors, setErrors]         = useState({});
  const [saved, setSaved]           = useState(false);

  // Custom fields for the contracts module
  const {
    fields: customFields,
    sections: customSections,
    loading: customFieldsLoading,
  } = useCustomFields('contracts');
  const [customValues, setCustomValues] = useState({});

  const [form, setForm] = useState({
    WorkerId:        preselectedWorkerId,
    ContractType:    'indefinite',
    StartDate:       today(),
    EndDate:         '',
    ProbationMonths: 3,
    Position:        '',
    Department:      '',
    WorkLocation:    'office',
    WorkHoursWeek:   40,
    BaseSalary:      '',
    HourSalary:      '',
    HourSalaryWeekend: '',
    HourSalaryHoliday: '',
    BankAccount:     '',
    BankName:        '',
    SalaryType:      'Bruto',
    Template:        '',
    TermsOfContract: '',
    Notes:           '',
  });

  useEffect(() => {
    Promise.all([
      apiService().get('workers'),
      apiService().get('department'),
      apiService().get('contractsTemplates'),
    ]).then(([w, d, t]) => {
      if (w?.success) setWorkers((w.data || []).filter(x => x.Active !== false));
      if (d?.success) setDepts(d.data || []);
      if (t?.success) setTemplates(t.data || []);
    });
  }, []);

  const set = (field) => (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  };

  const validate = () => {
    const e = {};
    if (!form.WorkerId)     e.WorkerId     = 'Zgjidhni punëtorin';
    if (!form.ContractType) e.ContractType = 'Zgjidhni llojin e kontratës';
    if (!form.StartDate)    e.StartDate    = 'Data e fillimit është e detyrueshme';
    if (!form.Position)     e.Position     = 'Pozita është e detyrueshme';
    if (!form.BaseSalary || Number(form.BaseSalary) < MIN_SALARY)
      e.BaseSalary = `Paga duhet të jetë ≥ ${MIN_SALARY}€ (minimumi ligjor KS)`;
    if (['fixed_term', 'seasonal', 'probationary', 'internship'].includes(form.ContractType) && !form.EndDate)
      e.EndDate = 'Data e mbarimit është e detyrueshme për këtë lloj kontrate';
    if (form.EndDate && form.StartDate && form.EndDate <= form.StartDate)
      e.EndDate = 'Data e mbarimit duhet të jetë pas datës së fillimit';
    if (Number(form.WorkHoursWeek) > 48)
      e.WorkHoursWeek = 'Orët e punës nuk mund të kalojnë 48h/javë (LP)';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setSaving(true);
    try {
      const payload = {
        WorkerId:           Number(form.WorkerId),
        ContractType:       form.ContractType,
        StartDate:          form.StartDate,
        EndDate:            form.EndDate || null,
        ProbationMonths:    form.ContractType === 'probationary' ? Number(form.ProbationMonths) : null,
        Position:           form.Position,
        Department:         form.Department ? Number(form.Department) : null,
        WorkLocation:       form.WorkLocation,
        WorkHoursWeek:      Number(form.WorkHoursWeek),
        Salary:             Number(form.BaseSalary),
        BaseSalary:         Number(form.BaseSalary),
        HourSalary:         form.HourSalary ? Number(form.HourSalary) : null,
        HourSalaryWeekend:  form.HourSalaryWeekend ? Number(form.HourSalaryWeekend) : null,
        HourSalaryHoliday:  form.HourSalaryHoliday ? Number(form.HourSalaryHoliday) : null,
        BankAccount:        form.BankAccount,
        BankName:           form.BankName,
        SalaryType:         form.SalaryType,
        Template:           form.Template ? Number(form.Template) : null,
        TermsOfContract:    form.TermsOfContract,
        Notes:              form.Notes,
        Status:             'active',
        CreatedAt:          new Date().toISOString(),
      };
      const res = await apiService().post('workerContract', payload);
      const newId =
        res?.data?.[0]?.id  || res?.data?.[0]?.Id  ||
        res?.data?.id       || res?.data?.Id       || null;
      if (newId && Object.keys(customValues).some(k => customValues[k] !== '' && customValues[k] != null)) {
        try {
          await configService.saveCustomFieldValues('contracts', newId, customValues);
        } catch (e) { console.error('Failed to save contract custom fields:', e); }
      }
      setSaved(true);
      setTimeout(() => history.push('/contractlist'), 1200);
    } catch (err) {
      setErrors({ _general: 'Ruajtja dështoi. Provo sërisht.' });
    } finally {
      setSaving(false);
    }
  };

  const selectedContractType = form.ContractType;
  const needsEndDate = ['fixed_term', 'seasonal', 'probationary', 'internship'].includes(selectedContractType);
  const showProbation = selectedContractType === 'probationary';

  return (
    <Box>
      <PageHeader
        title="Krijo Kontratë të Re"
        actions={
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/contractlist')} variant="outlined">
            Kthehu
          </Button>
        }
      />

      {saved && <Alert severity="success" sx={{ mb: 2 }}>Kontrata u ruajt me sukses! Duke ridrejtuar…</Alert>}
      {errors._general && <Alert severity="error" sx={{ mb: 2 }}>{errors._general}</Alert>}

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Të dhënat kryesore
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select fullWidth required label="Punëtori"
                value={form.WorkerId} onChange={set('WorkerId')}
                error={!!errors.WorkerId} helperText={errors.WorkerId}
              >
                {workers.map(w => (
                  <MenuItem key={w.Id} value={w.Id}>
                    {w.FirstName} {w.LastName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select fullWidth required label="Lloji i Kontratës"
                value={form.ContractType} onChange={set('ContractType')}
                error={!!errors.ContractType} helperText={errors.ContractType}
              >
                {CONTRACT_TYPES.map(ct => (
                  <MenuItem key={ct.value} value={ct.value}>{ct.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth required label="Pozita / Titulli i Punës"
                value={form.Position} onChange={set('Position')}
                error={!!errors.Position} helperText={errors.Position}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select fullWidth label="Departamenti"
                value={form.Department} onChange={set('Department')}
              >
                <MenuItem value="">— Pa departament —</MenuItem>
                {departments.map(d => (
                  <MenuItem key={d.Id} value={d.Id}>{d.Name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select fullWidth label="Vendndodhja e Punës"
                value={form.WorkLocation} onChange={set('WorkLocation')}
              >
                {WORK_LOCATIONS.map(l => (
                  <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth type="number" label="Orët e Punës / Javë"
                value={form.WorkHoursWeek} onChange={set('WorkHoursWeek')}
                inputProps={{ min: 1, max: 48 }}
                error={!!errors.WorkHoursWeek} helperText={errors.WorkHoursWeek || 'Standardi: 40h/javë (LP)'}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Datat
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth required type="date" label="Data e Fillimit"
                InputLabelProps={{ shrink: true }}
                value={form.StartDate} onChange={set('StartDate')}
                error={!!errors.StartDate} helperText={errors.StartDate}
              />
            </Grid>
            {needsEndDate && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth required type="date" label="Data e Mbarimit"
                  InputLabelProps={{ shrink: true }}
                  value={form.EndDate} onChange={set('EndDate')}
                  error={!!errors.EndDate} helperText={errors.EndDate || 'Alert do dërgohet 30 ditë para skadimit'}
                />
              </Grid>
            )}
            {showProbation && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select fullWidth label="Muajt e Provës (maks 6)"
                  value={form.ProbationMonths} onChange={set('ProbationMonths')}
                >
                  {[1,2,3,4,5,6].map(m => (
                    <MenuItem key={m} value={m}>{m} muaj</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Paga & Shpërblimet
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth required type="number" label={`Paga Bazë Bruto (min ${MIN_SALARY}€)`}
                value={form.BaseSalary} onChange={set('BaseSalary')}
                InputProps={{ endAdornment: '€' }}
                error={!!errors.BaseSalary} helperText={errors.BaseSalary || 'Paga minimale ligjore KS: 264€'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select fullWidth label="Lloji i Pagës"
                value={form.SalaryType} onChange={set('SalaryType')}
              >
                <MenuItem value="Bruto">Bruto</MenuItem>
                <MenuItem value="Neto">Neto</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth type="number" label="Orë Shtesë / Orë (€)"
                value={form.HourSalary} onChange={set('HourSalary')}
                helperText="≥125% e pagës/orë (LP Neni 25)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth type="number" label="Fundjavë / Orë (€)"
                value={form.HourSalaryWeekend} onChange={set('HourSalaryWeekend')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth type="number" label="Festë Zyrtare / Orë (€)"
                value={form.HourSalaryHoliday} onChange={set('HourSalaryHoliday')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Të dhënat bankare
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth label="IBAN / Numri i llogarisë"
                value={form.BankAccount} onChange={set('BankAccount')}
                helperText="Format: XK05 1212 ..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth label="Banka"
                value={form.BankName} onChange={set('BankName')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Template & Kushtet
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select fullWidth label="Template i Kontratës"
                value={form.Template} onChange={set('Template')}
              >
                <MenuItem value="">— Pa template —</MenuItem>
                {templates.map(t => (
                  <MenuItem key={t.Id} value={t.Id}>{t.Name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={4} label="Kushtet e Kontratës (Neni shtesë)"
                value={form.TermsOfContract} onChange={set('TermsOfContract')}
                placeholder="Kushte specifike, klauzola konfidencialiteti, etj."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2} label="Shënime"
                value={form.Notes} onChange={set('Notes')}
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            <strong>Validime aktive (LP):</strong> Paga minimale 264€ · Orë maksimale 40h/javë · Periudha provuese maks 6 muaj · Orë shtesë ≥125%
          </Alert>

          {/* Dynamic custom fields from Module Management */}
          <ModuleCustomFields
            moduleKey="contracts"
            fields={customFields}
            sections={customSections}
            values={customValues}
            onChange={setCustomValues}
            formType="create"
            loading={customFieldsLoading}
          />
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button
          variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving || saved}
        >
          {saving ? 'Duke ruajtur…' : 'Ruaj Kontratën'}
        </Button>
        <Button variant="outlined" onClick={() => history.push('/contractlist')}>
          Anulo
        </Button>
      </Stack>
    </Box>
  );
}
